import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { AnimatedSection } from "../shared/AnimatedSection";
import { SectionHeader } from "../shared/SectionHeader";
import { CTAButton } from "../shared/CTAButton";
import { UrgencyBanner } from "../shared/UrgencyBanner";
import { MicroTestimonial } from "../shared/MicroTestimonial";
import { Download, Shield, Users, CheckCircle2, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/utils/analytics";
import { toast } from "sonner";
import { practiceTypes, testimonials } from "@/config/checklisteConfig";
import { triggerDownload } from "@/utils/checklisteHelpers";
const formSchema = z.object({
  name: z.string().min(2, "Name muss mindestens 2 Zeichen lang sein").max(100),
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein").max(255),
  practice_type: z.string().min(1, "Bitte wählen Sie einen Praxistyp"),
  phone: z.string().optional()
});
type FormData = z.infer<typeof formSchema>;
const SuccessState = () => <AnimatedSection>
    <div className="bg-background/40 backdrop-blur-md border border-border/50 rounded-lg p-8 text-center">
      {/* Animated Checkmark */}
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center animate-scale-in">
        <CheckCircle2 className="w-12 h-12 text-accent animate-fade-in" strokeWidth={2.5} />
      </div>
      
      <h3 className="text-2xl sm:text-3xl font-bold mb-3 animate-fade-in">
        Download erfolgreich gestartet!
      </h3>
      
      <p className="text-muted-foreground mb-6 animate-fade-in">
        Ihre Cyber-Checkliste wird heruntergeladen und per E-Mail an Sie versendet.
      </p>
      
      {/* Manual Download Link */}
      <a href="/downloads/cyber-checkliste-arztpraxen.pdf" download="cyber-checkliste-arztpraxen.pdf" className="inline-flex items-center gap-2 text-accent hover:underline font-medium animate-fade-in hover-scale">
        <Download className="w-5 h-5" />
        Cyber-Checkliste PDF direkt herunterladen
      </a>
      
      <p className="text-sm text-muted-foreground mt-4 animate-fade-in">
        Prüfen Sie auch Ihren Download-Ordner.
      </p>
    </div>
  </AnimatedSection>;
export const LeadCaptureOptimized = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    setValue,
    watch
  } = useForm<FormData>({
    resolver: zodResolver(formSchema)
  });
  const practiceType = watch("practice_type");

  // Handler für Validierungs-Fehler
  const onError = () => {
    toast.error("Bitte füllen Sie alle Pflichtfelder aus!");
  };
  const onSubmit = async (data: FormData) => {
    // ✅ KRITISCH: Download MUSS synchron getriggert werden, bevor setIsSubmitting aufgerufen wird
    // Sonst verliert der Browser den Click-Context und blockiert den Download
    const link = document.createElement('a');
    link.href = '/downloads/cyber-checkliste-arztpraxen.pdf';
    link.download = 'cyber-checkliste-arztpraxen.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      // Schritt 1: Validierung
      setCurrentStep("Daten werden validiert...");
      setUploadProgress(25);
      await new Promise(resolve => setTimeout(resolve, 300));

      // Schritt 2: Speichern
      setCurrentStep("Daten werden gespeichert...");
      setUploadProgress(60);
      const {
        error
      } = await supabase.from("checklist_leads").insert({
        email: data.email.trim(),
        name: data.name.trim(),
        practice_type: data.practice_type,
        phone: data.phone?.trim() || null
      });
      if (error) throw error;

      // Send admin notification email (non-blocking)
      try {
        console.log('Invoking send-lead-notification edge function...');
        const {
          data: notificationData,
          error: notificationError
        } = await supabase.functions.invoke('send-lead-notification', {
          body: {
            name: data.name.trim(),
            email: data.email.trim(),
            practice_type: data.practice_type,
            phone: data.phone?.trim() || null
          }
        });
        if (notificationError) {
          console.error('Failed to send admin notification:', notificationError);
        } else {
          console.log('Admin notification sent successfully:', notificationData);
        }
      } catch (err) {
        console.error('Edge function invocation failed:', err);
      }

      // Send checklist PDF to user email (non-blocking backup)
      try {
        console.log('Invoking send-checklist-email edge function...');
        const {
          data: checklistData,
          error: checklistEmailError
        } = await supabase.functions.invoke('send-checklist-email', {
          body: {
            name: data.name.trim(),
            email: data.email.trim(),
            practice_type: data.practice_type
          }
        });
        if (checklistEmailError) {
          console.error('Failed to send checklist email:', checklistEmailError);
        } else {
          console.log('Checklist email sent successfully:', checklistData);
        }
      } catch (err) {
        console.error('Checklist email edge function invocation failed:', err);
      }
      trackEvent("lead_captured", {
        practice_type: data.practice_type,
        source: "checklist_landing"
      });

      // Schritt 3: Abschluss
      setCurrentStep("Fast fertig...");
      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 200));
      setIsSuccess(true);
      toast.success("Sie erhalten in Kürze eine E-Mail mit der Checkliste!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Ein Fehler ist aufgetreten. Download wird trotzdem gestartet.");

      // ✅ Notfall-Download auch bei Fehler
      window.open('/downloads/cyber-checkliste-arztpraxen.pdf', '_blank');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <section id="lead-form" className="py-20 sm:py-24 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SuccessState />
        </div>
      </section>;
  }
  return <section id="lead-form" className="py-20 sm:py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-12 sm:mb-14 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Download className="w-5 h-5 text-accent" />
            <span className="text-sm font-semibold text-accent">Kostenloser Download</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight">
            Jetzt Cyber-Checkliste sichern
          </h2>
          
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto leading-6">
            Dauert nur 30 Sekunden – Sofortiger Zugriff
          </p>
        </div>

        <UrgencyBanner className="px-[46px] mx-[245px]" />

        <div className="max-w-2xl mx-auto mt-8 sm:mt-10 lg:mt-12">
            <div className="bg-card/90 backdrop-blur-md border border-border/50 rounded-lg p-6 sm:p-8 lg:p-10 shadow-xl relative">
              
              {/* Progress Overlay */}
              {isSubmitting && <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center z-50 rounded-lg animate-fade-in">
                  <div className="w-full max-w-md px-8">
                    <Progress value={uploadProgress} className="h-3 mb-4" />
                    <p className="text-center text-sm text-muted-foreground animate-pulse">
                      {currentStep}
                    </p>
                  </div>
                </div>}
              
              <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-5">
                
                {/* Form Fields - Einspaltig */}
                <div>
                  <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
                  <Input id="name" {...register("name")} placeholder="Dr. Max Mustermann" className="h-12 bg-card/80 mt-2" />
                  {errors.name && <p className="text-sm text-destructive mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium">E-Mail *</Label>
                  <Input id="email" type="email" {...register("email")} placeholder="praxis@beispiel.de" className="h-12 bg-card/80 mt-2" />
                  {errors.email && <p className="text-sm text-destructive mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label htmlFor="practice_type" className="text-sm font-medium">Praxistyp *</Label>
                  <Select value={practiceType} onValueChange={value => setValue("practice_type", value)}>
                    <SelectTrigger id="practice_type" className="h-12 bg-card/80 mt-2">
                      <SelectValue placeholder="Bitte wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {practiceTypes.map(type => <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.practice_type && <p className="text-sm text-destructive mt-1">{errors.practice_type.message}</p>}
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">Telefon (optional)</Label>
                  <Input id="phone" type="tel" {...register("phone")} placeholder="+49 123 456789" className="h-12 bg-card/80 mt-2" />
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <CTAButton icon={Download} disabled={isSubmitting} type="submit" className="w-full group justify-center text-lg px-8 py-6 h-auto">
                    {isSubmitting ? <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
                        Wird gesendet...
                      </> : <>
                        <Download className="mr-3 w-6 h-6 group-hover:animate-bounce" />
                        JETZT KOSTENLOS HERUNTERLADEN
                      </>}
                  </CTAButton>
                </div>

                {/* What's Included & Trust Signals - Unter dem Formular */}
                <div className="mt-6 pt-6 border-t border-border/50 space-y-4">
                  <h4 className="font-semibold text-base text-center">Das erhalten Sie:</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>15 praxiserprobte Sicherheits-Checks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>Sofort umsetzbare Maßnahmen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>DSGVO-konforme Sicherheitstipps</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span>PDF zum Ausdrucken & Abhaken</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="font-semibold">Sofortiger Download</span>
                    </li>
                  </ul>

                  <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground pt-4">
                    <span className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-accent" />
                      Kostenlos
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-accent" />
                      500+ Downloads
                    </span>
                    <span className="flex items-center gap-1">
                      <span>🔒</span>
                      Kein Spam
                    </span>
                  </div>
                </div>
              </form>

            </div>
          </div>

        {/* Micro Testimonials Below Form */}
        <div className="max-w-3xl mx-auto mt-8 sm:mt-10 lg:mt-12 flex flex-wrap justify-center gap-4">
          {testimonials.slice(0, 3).map((testimonial, index) => <div key={index} className="w-full sm:w-[calc(33.333%-1rem)] min-w-[250px]">
              
            </div>)}
        </div>
      </div>
    </section>;
};